// Pure lifecycle count deltas for Song Library tab labels.
// Optimistic patches must update Active / Archived / All immediately so the
// UI never shows a moved row while tabs still describe the previous state.
// Delayed LIST reconcile must be generation-guarded and must not accept a
// stale count response that undoes the optimistic snapshot.

export type LifecycleCountOp = "archive" | "restore" | "delete";

export type LifecycleCountBucket = "active" | "archived" | "all";

export interface LifecycleCountSnapshot {
  active: number;
  archived: number;
  all: number;
}

/** Per-operation deltas applied to a single countTracks cache entry. */
export function lifecycleCountDelta(
  op: LifecycleCountOp,
  status: LifecycleCountBucket | string | undefined,
): number {
  const bucket =
    status === "archived" || status === "all" || status === "active"
      ? status
      : "active";

  if (op === "archive") {
    if (bucket === "active") return -1;
    if (bucket === "archived") return 1;
    return 0; // all unchanged
  }
  if (op === "restore") {
    if (bucket === "active") return 1;
    if (bucket === "archived") return -1;
    return 0;
  }
  // permanent delete (archived → tombstone / gone from library)
  if (bucket === "archived") return -1;
  if (bucket === "all") return -1;
  return 0;
}

export function applyLifecycleCountDelta(
  current: number,
  delta: number,
): number {
  if (typeof current !== "number" || !Number.isFinite(current) || current < 0) {
    return current;
  }
  return Math.max(0, current + delta);
}

export function applyLifecycleOpToSnapshot(
  snap: LifecycleCountSnapshot,
  op: LifecycleCountOp,
  times = 1,
): LifecycleCountSnapshot {
  const n = Math.max(0, Math.floor(times));
  let active = snap.active;
  let archived = snap.archived;
  let all = snap.all;
  for (let i = 0; i < n; i += 1) {
    active = applyLifecycleCountDelta(active, lifecycleCountDelta(op, "active"));
    archived = applyLifecycleCountDelta(
      archived,
      lifecycleCountDelta(op, "archived"),
    );
    all = applyLifecycleCountDelta(all, lifecycleCountDelta(op, "all"));
  }
  return { active, archived, all };
}

/**
 * Mutations must NOT immediately invalidate Tracks LIST — that refetches
 * countTracks against a lagging projection and can overwrite optimistic
 * totals with stale values. Reconcile is delayed + generation-guarded.
 */
export const TRACKS_LIFECYCLE_IMMEDIATE_LIST_INVALIDATION = false as const;

export const TRACKS_RECONCILE_DELAY_MS = 2500;

/** Extra retries when projection count still lags expected post-op totals. */
export const TRACKS_RECONCILE_RETRY_MS = 1500;
export const TRACKS_RECONCILE_MAX_ATTEMPTS = 4;

/**
 * True when a server count response is at least as far along as the optimistic
 * expected snapshot for the given op direction (never accept a regression).
 */
export function countResponseMatchesExpected(
  status: LifecycleCountBucket | string | undefined,
  serverCount: number,
  expected: LifecycleCountSnapshot,
): boolean {
  if (typeof serverCount !== "number" || !Number.isFinite(serverCount)) {
    return false;
  }
  if (status === "active") return serverCount >= expected.active;
  if (status === "archived") {
    // After restore/delete, archived should not be higher than expected.
    return serverCount <= expected.archived;
  }
  if (status === "all") return serverCount <= expected.all;
  return true;
}

/**
 * After restore, Active must reach expected; after archive, Active must drop.
 * Used by generation-guarded reconcile to reject stale refetches.
 */
export function isStaleCountVersusExpected(
  status: LifecycleCountBucket | string | undefined,
  serverCount: number,
  expected: LifecycleCountSnapshot,
  op: LifecycleCountOp,
): boolean {
  if (typeof serverCount !== "number" || !Number.isFinite(serverCount) || serverCount < 0) {
    return true;
  }
  if (op === "restore") {
    if (status === "active") return serverCount < expected.active;
    if (status === "archived") return serverCount > expected.archived;
    return false;
  }
  if (op === "archive") {
    if (status === "active") return serverCount > expected.active;
    if (status === "archived") return serverCount < expected.archived;
    return false;
  }
  // delete
  if (status === "archived") return serverCount > expected.archived;
  if (status === "all") return serverCount > expected.all;
  return false;
}

/**
 * Resolve a Song Library lifecycle tab label count.
 *
 * A complete cached list page (no pageToken) is authoritative vs a lagging
 * countTracks projection — that is what produced Active(2)/Archived(0) while
 * the Active table already showed one row after Archive. Incomplete pages
 * still use the historical floor: never show a tab total below the loaded
 * rows for that lifecycle.
 */
export function resolveLifecycleTabCount(args: {
  serverCount: number | null | undefined;
  loadedCount: number;
  hasListData: boolean;
  listComplete: boolean;
}): number | undefined {
  const loaded =
    typeof args.loadedCount === "number" &&
    Number.isFinite(args.loadedCount) &&
    args.loadedCount >= 0
      ? Math.floor(args.loadedCount)
      : 0;
  const server =
    typeof args.serverCount === "number" &&
    Number.isFinite(args.serverCount) &&
    args.serverCount >= 0
      ? Math.floor(args.serverCount)
      : null;

  if (args.hasListData && args.listComplete) {
    return loaded;
  }
  if (server != null) {
    return args.hasListData ? Math.max(server, loaded) : server;
  }
  if (args.hasListData) return loaded;
  return undefined;
}
